import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Users, Plus, Building2, ArrowLeft, Percent, DollarSign,
  PieChart, TrendingUp, Trash2, Edit, Calculator, Crown,
  User, Briefcase, ChevronRight, AlertCircle
} from 'lucide-react';

export default function PartnershipManager() {
  const navigate = useNavigate();
  const { user: authUser, token } = useAuth();
  const [user, setUser] = useState(null);
  const [partnerships, setPartnerships] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [distributionResult, setDistributionResult] = useState(null);
  const [distributionAmount, setDistributionAmount] = useState('');

  // Form state for creating partnership
  const [formData, setFormData] = useState({
    name: '',
    deal_id: '',
    structure_type: 'pref_return',
    total_equity_required: '',
    acquisition_fee_percent: '',
    asset_management_fee_percent: '',
    notes: '',
    partners: [
      { name: '', role: 'GP', capital_contribution: '', equity_percentage: '', preferred_return: '8' },
      { name: '', role: 'LP', capital_contribution: '', equity_percentage: '', preferred_return: '8' }
    ]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, partnershipsRes, dealsRes] = await Promise.all([
        axios.get(`${API}/users/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/partnerships`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { partnerships: [] } })),
        axios.get(`${API}/deals`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setUser(userRes.data);
      setPartnerships(partnershipsRes.data.partnerships || []);
      setDeals(dealsRes.data.deals || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Partnership Mode requires Pro Investor');
      }
    } finally {
      setLoading(false);
    }
  };

  const normalizedTier = user?.tier === 'light' ? 'free' : (user?.tier || 'free');
  const hasPartnershipAccess = normalizedTier === 'pro' || normalizedTier === 'developer';

  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const addPartner = () => {
    setFormData(prev => ({
      ...prev,
      partners: [...prev.partners, { name: '', role: 'LP', capital_contribution: '', equity_percentage: '', preferred_return: '8' }]
    }));
  };

  const removePartner = (index) => {
    if (formData.partners.length <= 2) {
      toast.error('Minimum 2 partners required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      partners: prev.partners.filter((_, i) => i !== index)
    }));
  };

  const updatePartner = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      partners: prev.partners.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const handleCreatePartnership = async () => {
    // Validate
    const totalEquity = formData.partners.reduce((sum, p) => sum + (parseFloat(p.equity_percentage) || 0), 0);
    if (Math.abs(totalEquity - 100) > 0.01) {
      toast.error(`Equity percentages must sum to 100%. Current: ${totalEquity}%`);
      return;
    }

    const totalCapital = formData.partners.reduce((sum, p) => sum + (parseFloat(p.capital_contribution) || 0), 0);
    const requiredEquity = parseFloat(formData.total_equity_required) || 0;
    if (Math.abs(totalCapital - requiredEquity) > 0.01) {
      toast.error(`Capital contributions must equal total equity required`);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        deal_id: formData.deal_id,
        structure_type: formData.structure_type,
        total_equity_required: parseFloat(formData.total_equity_required),
        acquisition_fee_percent: formData.acquisition_fee_percent ? parseFloat(formData.acquisition_fee_percent) : null,
        asset_management_fee_percent: formData.asset_management_fee_percent ? parseFloat(formData.asset_management_fee_percent) : null,
        notes: formData.notes || null,
        partners: formData.partners.map(p => ({
          name: p.name,
          role: p.role,
          capital_contribution: parseFloat(p.capital_contribution),
          equity_percentage: parseFloat(p.equity_percentage),
          preferred_return: p.preferred_return ? parseFloat(p.preferred_return) : null
        }))
      };

      await axios.post(`${API}/partnerships`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Partnership structure created!');
      setShowCreateModal(false);
      fetchData();
      
      // Reset form
      setFormData({
        name: '',
        deal_id: '',
        structure_type: 'pref_return',
        total_equity_required: '',
        acquisition_fee_percent: '',
        asset_management_fee_percent: '',
        notes: '',
        partners: [
          { name: '', role: 'GP', capital_contribution: '', equity_percentage: '', preferred_return: '8' },
          { name: '', role: 'LP', capital_contribution: '', equity_percentage: '', preferred_return: '8' }
        ]
      });
    } catch (error) {
      console.error('Error creating partnership:', error);
      toast.error(error.response?.data?.detail || 'Failed to create partnership');
    }
  };

  const handleDeletePartnership = async (partnershipId) => {
    if (!window.confirm('Are you sure you want to delete this partnership structure?')) return;

    try {
      await axios.delete(`${API}/partnerships/${partnershipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partnership deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete partnership');
    }
  };

  const handleCalculateDistribution = async () => {
    if (!distributionAmount || !selectedPartnership) return;

    try {
      const response = await axios.post(`${API}/partnerships/calculate-distribution`, {
        partnership_id: selectedPartnership.id,
        total_cash_to_distribute: parseFloat(distributionAmount),
        distribution_type: 'operating'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDistributionResult(response.data);
    } catch (error) {
      toast.error('Failed to calculate distribution');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!hasPartnershipAccess) {
    return (
      <div className="min-h-screen bg-slate-950 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-400 mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="glass-card rounded-2xl p-12 text-center">
            <Crown className="h-16 w-16 text-amber-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-4">Partnership/Syndication Mode</h1>
            <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
              Model deals with multiple investors, custom equity splits, preferred returns, and waterfall structures.
            </p>
            
            <div className="bg-slate-800/50 rounded-xl p-6 mb-8 max-w-md mx-auto">
              <h3 className="text-amber-500 font-semibold mb-4">Pro Investor Features:</h3>
              <ul className="text-left text-slate-300 space-y-2">
                <li className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-amber-500" />
                  Multiple GP/LP investor tracking
                </li>
                <li className="flex items-center gap-2">
                  <Percent className="h-4 w-4 text-amber-500" />
                  Preferred return structures
                </li>
                <li className="flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-amber-500" />
                  Waterfall distribution modeling
                </li>
                <li className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-amber-500" />
                  Automatic distribution calculations
                </li>
              </ul>
            </div>

            <Button 
              onClick={() => navigate('/upgrade')}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8 py-6 text-lg"
            >
              Upgrade to Pro Investor
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-400">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <Users className="h-7 w-7 text-amber-500" />
                Partnership/Syndication Manager
              </h1>
              <p className="text-slate-400 text-sm">Model multi-investor deal structures</p>
            </div>
          </div>

          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                <Plus className="h-4 w-4 mr-2" />
                New Partnership
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <Users className="h-5 w-5 text-amber-500" />
                  Create Partnership Structure
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Partnership Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Oakwood Apartments Syndication"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Select Deal</Label>
                    <Select
                      value={formData.deal_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, deal_id: value }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue placeholder="Choose a deal" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {deals.map(deal => (
                          <SelectItem key={deal.id} value={deal.id} className="text-white">
                            {deal.deal_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-300">Structure Type</Label>
                    <Select
                      value={formData.structure_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, structure_type: value }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="equity_split" className="text-white">Simple Equity Split</SelectItem>
                        <SelectItem value="pref_return" className="text-white">Preferred Return</SelectItem>
                        <SelectItem value="waterfall" className="text-white">Waterfall Structure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Total Equity Required ($)</Label>
                    <Input
                      type="number"
                      value={formData.total_equity_required}
                      onChange={(e) => setFormData(prev => ({ ...prev, total_equity_required: e.target.value }))}
                      placeholder="100000"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Acquisition Fee (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.acquisition_fee_percent}
                      onChange={(e) => setFormData(prev => ({ ...prev, acquisition_fee_percent: e.target.value }))}
                      placeholder="1.5"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                </div>

                {/* Partners */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-slate-300 text-lg">Partners/Investors</Label>
                    <Button variant="outline" size="sm" onClick={addPartner} className="text-amber-500 border-amber-500/50">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Partner
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {formData.partners.map((partner, index) => (
                      <div key={index} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                        <div className="grid grid-cols-6 gap-3">
                          <div className="col-span-2">
                            <Label className="text-xs text-slate-400">Name</Label>
                            <Input
                              value={partner.name}
                              onChange={(e) => updatePartner(index, 'name', e.target.value)}
                              placeholder="Partner Name"
                              className="bg-slate-900 border-slate-600 text-white mt-1 h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Role</Label>
                            <Select
                              value={partner.role}
                              onValueChange={(value) => updatePartner(index, 'role', value)}
                            >
                              <SelectTrigger className="bg-slate-900 border-slate-600 text-white mt-1 h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="GP" className="text-white">GP</SelectItem>
                                <SelectItem value="LP" className="text-white">LP</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Capital ($)</Label>
                            <Input
                              type="number"
                              value={partner.capital_contribution}
                              onChange={(e) => updatePartner(index, 'capital_contribution', e.target.value)}
                              placeholder="50000"
                              className="bg-slate-900 border-slate-600 text-white mt-1 h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Equity (%)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={partner.equity_percentage}
                              onChange={(e) => updatePartner(index, 'equity_percentage', e.target.value)}
                              placeholder="50"
                              className="bg-slate-900 border-slate-600 text-white mt-1 h-9"
                            />
                          </div>
                          <div className="flex items-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removePartner(index)}
                              className="text-red-400 hover:text-red-300 h-9"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {formData.structure_type === 'pref_return' && (
                          <div className="mt-2">
                            <Label className="text-xs text-slate-400">Preferred Return (%)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={partner.preferred_return}
                              onChange={(e) => updatePartner(index, 'preferred_return', e.target.value)}
                              placeholder="8"
                              className="bg-slate-900 border-slate-600 text-white mt-1 h-9 w-32"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Validation Summary */}
                  <div className="mt-3 p-3 bg-slate-800 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Total Equity:</span>
                      <span className={formData.partners.reduce((sum, p) => sum + (parseFloat(p.equity_percentage) || 0), 0) === 100 ? 'text-emerald-500' : 'text-amber-500'}>
                        {formData.partners.reduce((sum, p) => sum + (parseFloat(p.equity_percentage) || 0), 0)}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Total Capital:</span>
                      <span className="text-white">
                        {formatCurrency(formData.partners.reduce((sum, p) => sum + (parseFloat(p.capital_contribution) || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes about this partnership structure..."
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-slate-700">
                    Cancel
                  </Button>
                  <Button onClick={handleCreatePartnership} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                    Create Partnership
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Partnerships List */}
        {partnerships.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <Users className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Partnerships Yet</h3>
            <p className="text-slate-400 mb-6">Create your first partnership structure to model multi-investor deals.</p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <Plus className="h-4 w-4 mr-2" />
              Create Partnership
            </Button>
          </div>
        ) : (
          <div className="grid gap-6">
            {partnerships.map((partnership) => (
              <div key={partnership.id} className="glass-card rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                      <Users className="h-5 w-5 text-amber-500" />
                      {partnership.name}
                    </h3>
                    <p className="text-slate-400 text-sm flex items-center gap-2 mt-1">
                      <Building2 className="h-4 w-4" />
                      {partnership.deal_name}
                      <span className="mx-2">•</span>
                      <span className="capitalize">{partnership.structure_type.replace('_', ' ')}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedPartnership(partnership);
                        setDistributionAmount('');
                        setDistributionResult(null);
                        setShowDistributionModal(true);
                      }}
                      className="text-cyan-400 border-cyan-500/50"
                    >
                      <Calculator className="h-4 w-4 mr-1" />
                      Calculate
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePartnership(partnership.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-xs text-slate-400">Total Equity</div>
                    <div className="text-lg font-bold text-emerald-500">
                      {formatCurrency(partnership.total_equity_required)}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-xs text-slate-400">Partners</div>
                    <div className="text-lg font-bold text-cyan-500">
                      {partnership.partners?.length || 0}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-xs text-slate-400">GPs</div>
                    <div className="text-lg font-bold text-amber-500">
                      {partnership.partners?.filter(p => p.role === 'GP').length || 0}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-xs text-slate-400">LPs</div>
                    <div className="text-lg font-bold text-violet-500">
                      {partnership.partners?.filter(p => p.role === 'LP').length || 0}
                    </div>
                  </div>
                </div>

                {/* Partners Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Partner</th>
                        <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Role</th>
                        <th className="text-right py-2 px-3 text-xs text-slate-400 font-medium">Capital</th>
                        <th className="text-right py-2 px-3 text-xs text-slate-400 font-medium">Equity</th>
                        <th className="text-right py-2 px-3 text-xs text-slate-400 font-medium">Pref Return</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnership.partners?.map((partner, i) => (
                        <tr key={i} className="border-b border-slate-800">
                          <td className="py-2 px-3 text-white font-medium">{partner.name}</td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                              partner.role === 'GP' ? 'bg-amber-500/20 text-amber-500' : 'bg-violet-500/20 text-violet-500'
                            }`}>
                              {partner.role}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right text-slate-300">{formatCurrency(partner.capital_contribution)}</td>
                          <td className="py-2 px-3 text-right text-slate-300">{partner.equity_percentage}%</td>
                          <td className="py-2 px-3 text-right text-slate-300">{partner.preferred_return ? `${partner.preferred_return}%` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Distribution Calculation Modal */}
        <Dialog open={showDistributionModal} onOpenChange={setShowDistributionModal}>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Calculator className="h-5 w-5 text-cyan-500" />
                Calculate Distribution
              </DialogTitle>
            </DialogHeader>

            {selectedPartnership && (
              <div className="space-y-6 py-4">
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="text-sm text-slate-400">Partnership</div>
                  <div className="text-lg font-semibold">{selectedPartnership.name}</div>
                </div>

                <div>
                  <Label className="text-slate-300">Cash to Distribute ($)</Label>
                  <Input
                    type="number"
                    value={distributionAmount}
                    onChange={(e) => setDistributionAmount(e.target.value)}
                    placeholder="20000"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>

                <Button
                  onClick={handleCalculateDistribution}
                  disabled={!distributionAmount}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate Distribution
                </Button>

                {distributionResult && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-white">Distribution Breakdown</h4>
                    
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                        <div className="text-xs text-slate-400">Total Distributed</div>
                        <div className="text-lg font-bold text-emerald-500">
                          {formatCurrency(distributionResult.summary.total_distributed)}
                        </div>
                      </div>
                      <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                        <div className="text-xs text-slate-400">GP Total ({distributionResult.summary.gp_percent}%)</div>
                        <div className="text-lg font-bold text-amber-500">
                          {formatCurrency(distributionResult.summary.gp_total)}
                        </div>
                      </div>
                      <div className="p-3 bg-violet-500/10 rounded-lg border border-violet-500/20">
                        <div className="text-xs text-slate-400">LP Total ({distributionResult.summary.lp_percent}%)</div>
                        <div className="text-lg font-bold text-violet-500">
                          {formatCurrency(distributionResult.summary.lp_total)}
                        </div>
                      </div>
                    </div>

                    {/* Waterfall Breakdown */}
                    {distributionResult.waterfall_breakdown && (
                      <div className="p-3 bg-slate-800/50 rounded-lg">
                        <div className="text-sm font-medium text-slate-300 mb-2">Waterfall</div>
                        {distributionResult.waterfall_breakdown.map((tier, i) => (
                          <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-700 last:border-0">
                            <span className="text-slate-400">{tier.tier}</span>
                            <span className="text-white">{formatCurrency(tier.distributed)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Partner Distributions */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-2 px-2 text-xs text-slate-400">Partner</th>
                            <th className="text-right py-2 px-2 text-xs text-slate-400">Pref Return</th>
                            <th className="text-right py-2 px-2 text-xs text-slate-400">Profit Share</th>
                            <th className="text-right py-2 px-2 text-xs text-slate-400">Total</th>
                            <th className="text-right py-2 px-2 text-xs text-slate-400">Return %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {distributionResult.distributions.map((dist, i) => (
                            <tr key={i} className="border-b border-slate-800">
                              <td className="py-2 px-2">
                                <span className="text-white font-medium">{dist.partner_name}</span>
                                <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
                                  dist.role === 'GP' ? 'bg-amber-500/20 text-amber-500' : 'bg-violet-500/20 text-violet-500'
                                }`}>{dist.role}</span>
                              </td>
                              <td className="py-2 px-2 text-right text-slate-300">
                                {dist.preferred_return_amount ? formatCurrency(dist.preferred_return_amount) : '-'}
                              </td>
                              <td className="py-2 px-2 text-right text-slate-300">
                                {formatCurrency(dist.profit_share_amount)}
                              </td>
                              <td className="py-2 px-2 text-right text-emerald-500 font-semibold">
                                {formatCurrency(dist.total_distribution)}
                              </td>
                              <td className="py-2 px-2 text-right text-cyan-500">
                                {dist.effective_return_percent}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
